import { useEffect, useRef, useState } from "react";
import { Switch } from "@headlessui/react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useStore } from "../contexts/StoreContext";
import { fetchNotifications, fetchPasses, fetchBranches } from "../api/queries";
import {
  createNotification,
  updateNotification,
  deleteNotification,
} from "../api/mutations";
import {
  PlusIcon,
  PencilIcon,
  TrashIcon,
  BellIcon,
} from "@heroicons/react/24/outline";
import { useLanguage } from "../contexts/LanguageContext";
import { Link } from "react-router-dom";
import EmptyState from "../components/EmptyState";
import DeleteAlert from "../components/DeleteAlert";
import TrialEndedAlert from "../components/TrialEndedAlert";
import Header from "../components/Header";
import Button from "../components/Button";
import Drawer from "../blocks/drawer";
import PlanAlert from "../components/PlanAlert";
import { timeZoneLabel } from "../utils/timeZones";

/**
 * A value the merchant cannot change, shown as text rather than a disabled
 * input. A greyed-out select still looks clickable, and clicking one that does
 * nothing reads as a bug.
 */
// eslint-disable-next-line react/prop-types -- no propTypes in this codebase (CLAUDE.md)
function ReadOnlyValue({ children }) {
  return (
    <p className="mt-1 rounded-md bg-gray-50 px-3 py-2 text-sm text-gray-600 ring-1 ring-inset ring-gray-200">
      {children}
    </p>
  );
}

export default function Notifications() {
  const { selectedStore, canManageStore } = useStore();
  const queryClient = useQueryClient();
  const { t } = useLanguage();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingNotification, setEditingNotification] = useState(null);
  const isDefaultNotification = editingNotification?.kind === "default";
  const [deleteNotificationId, setDeleteNotificationId] = useState(null);
  const [isDeleteAlertOpen, setIsDeleteAlertOpen] = useState(false);
  const [isTrialEndedAlertOpen, setIsTrialEndedAlertOpen] = useState(false);

  const [formData, setFormData] = useState({
    pass_id: "",
    title: "",
    description: "",
    scheduled_at: "",
    kind: "auto",
    is_active: true,
    data: {},
  });
  const [selectedPassId, setSelectedPassId] = useState("all");
  const [error, setError] = useState("");
  const [isPlanAlertOpen, setIsPlanAlertOpen] = useState(false);
  const isStarterPlan = selectedStore?.current_plan === "starter";

  // Fetch notifications
  const { data: notifications = [], isLoading: isLoadingNotifications } =
    useQuery({
      queryKey: ["notifications", selectedStore?.id],
      queryFn: () => fetchNotifications(selectedStore?.id),
      enabled: !!selectedStore?.id,
    });

  // Fetch passes for the dropdown
  const { data: passes = [] } = useQuery({
    queryKey: ["passes", selectedStore?.id],
    queryFn: () => fetchPasses(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  // Fetch branches
  const { data: branches = [] } = useQuery({
    queryKey: ["branches", selectedStore?.id],
    queryFn: () => fetchBranches(selectedStore?.id),
    enabled: !!selectedStore?.id,
  });

  // Create notification mutation
  const createNotificationMutation = useMutation({
    mutationFn: createNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications", selectedStore?.id],
      });
      handleCloseModal();
    },
  });

  // Update notification mutation
  const updateNotificationMutation = useMutation({
    mutationFn: updateNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications", selectedStore?.id],
      });
      handleCloseModal();
    },
  });

  // Delete notification mutation
  const deleteNotificationMutation = useMutation({
    mutationFn: deleteNotification,
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["notifications", selectedStore?.id],
      });
    },
  });

  // Check if store is in trial period (15 days from creation)
  const isInTrialPeriod = () => {
    if (!selectedStore?.trial_expires_at) return false;
    const trialEndDate = new Date(selectedStore.trial_expires_at);
    trialEndDate.setDate(trialEndDate.getDate());
    return new Date() < trialEndDate;
  };

  const handleOpenModal = (notification = null) => {
    // Check subscription limits
    if (
      selectedStore?.subscription_status !== "active" &&
      selectedStore?.subscription_status !== "trialing" &&
      selectedStore?.subscription_status !== "past_due" &&
      !isInTrialPeriod()
    ) {
      setIsTrialEndedAlertOpen(true);
      return;
    }
    if (
      notification &&
      isStarterPlan &&
      notification.kind === "auto" &&
      !starterAllowedAutoTriggers.includes(notification.title)
    ) {
      setIsPlanAlertOpen(true);
      return;
    }
    if (notification) {
      setEditingNotification(notification);
      setFormData({
        pass_id: notification.pass?.id?.toString() || "all",
        title: notification.title,
        description: notification.description,
        kind: notification.kind,
        is_active: notification.is_active ?? true,
        data: notification.data,
      });
    } else {
      setEditingNotification(null);
      setFormData({
        pass_id: "",
        title: "",
        description: "",
        kind: "auto",
        is_active: true,
        data: {},
      });
    }
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingNotification(null);
    setError("");
    setFormData({
      pass_id: "",
      title: "",
      description: "",
      kind: "auto",
      is_active: true,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const notificationData = {
      ...formData,
      ...(isGeoTrigger(formData.title) ? { is_active: true } : {}),
    };

    if (
      isStarterPlan &&
      notificationData.kind === "auto" &&
      notificationData.title &&
      !starterAllowedAutoTriggers.includes(notificationData.title)
    ) {
      setIsPlanAlertOpen(true);
      return;
    }

    if (editingNotification) {
      await updateNotificationMutation.mutateAsync({
        storeId: selectedStore.id,
        notificationId: editingNotification.id,
        notificationData,
      });
    } else {
      await createNotificationMutation.mutateAsync({
        storeId: selectedStore.id,
        notificationData,
      });
    }
  };

  const handleDelete = async (notificationId) => {
    if (window.confirm(t("notifications.deleteConfirm"))) {
      await deleteNotificationMutation.mutateAsync({
        storeId: selectedStore.id,
        notificationId,
      });
    }
  };

  // Which %placeholders% the backend substitutes for a given trigger. Mirrors
  // build_notification_field (apple_wallet/service.rb), render_notification_body
  // (google_wallet/update_pass_job.rb) and PointsExpiringReminderJob#render_body.
  // The two geo triggers are deliberately absent: their text is written onto the
  // pass verbatim (build_locations), nothing is substituted. Default
  // notifications are matched by suffix so every card type resolves.
  const placeholdersFor = (title) => {
    const auto = {
      "notifications.auto.birthday": ["earned_amount", "total_amount", "card_name"],
      "notifications.auto.reach_points": ["earned_amount", "total_amount", "card_name"],
      "notifications.auto.eligible_reward": ["earned_amount", "total_amount", "card_name"],
      "notifications.auto.tier_changes": ["total_amount", "card_name"],
      "notifications.auto.points_expiring": ["expiring_amount", "total_amount", "days", "card_name"],
    };
    if (auto[title]) return auto[title];
    if (title?.endsWith(".increased")) return ["earned_amount", "total_amount", "card_name"];
    if (title?.endsWith(".decreased")) return ["spent_amount", "total_amount", "card_name"];
    if (title?.endsWith(".used")) return ["discount_amount", "card_name"];
    return [];
  };

  // Starter copy for triggers whose field would otherwise open empty. The
  // merchant edits it like any other text; it is only ever written when the
  // field is blank or still holds a prefill we put there, so a merchant's own
  // words are never overwritten by switching triggers.
  const prefillFor = (title) =>
    title === "notifications.auto.points_expiring"
      ? t("notifications.prefill.points_expiring")
      : "";

  // The reminder job runs at 10:30 on the store's local clock, or 10:30 UTC
  // when the store has no time zone (backend PointsExpiringReminderJob).
  const reminderSchedule = selectedStore?.time_zone
    ? t("notifications.form.reminderScheduleStore", {
        timeZone: timeZoneLabel(selectedStore.time_zone),
      })
    : t("notifications.form.reminderScheduleUtc");

  const descriptionRef = useRef(null);

  // Grow the message field to fit its text. Two rows hid the end of even the
  // prefilled sentence; a merchant should never have to scroll inside the box
  // to read what their customers will get. Runs on every change of the text —
  // typing, a placeholder chip, the prefill, opening an existing notification —
  // and again when the drawer opens, since the element only exists then.
  useEffect(() => {
    const el = descriptionRef.current;
    if (!el) return;
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }, [formData.description, isModalOpen]);

  // Drop the token at the caret (replacing any selection) rather than at the
  // end, and put the caret back after it once React has re-rendered the value.
  const insertPlaceholder = (name) => {
    const token = `%${name}%`;
    const el = descriptionRef.current;
    const text = formData.description || "";
    const start = el?.selectionStart ?? text.length;
    const end = el?.selectionEnd ?? text.length;
    const next = text.slice(0, start) + token + text.slice(end);
    setFormData((prev) => ({ ...prev, description: next }));
    const caret = start + token.length;
    window.setTimeout(() => {
      if (!el) return;
      el.focus();
      el.setSelectionRange(caret, caret);
    }, 0);
  };

  const notificationTypes = ["default", "auto"];
  // Immediately is a stored 0; 1..15 is what the selector offers. The backend
  // caps the same range and reads anything outside it as Immediately, so a
  // value written through the API rather than this form is shown clamped here
  // rather than as an option that does not exist.
  const MIN_DELAY_DAYS = 1;
  const MAX_DELAY_DAYS = 15;
  const DEFAULT_DELAY_DAYS = 3;
  const DELAY_DAY_OPTIONS = Array.from(
    { length: MAX_DELAY_DAYS - MIN_DELAY_DAYS + 1 },
    (_, i) => i + MIN_DELAY_DAYS,
  );

  const rawDelay = formData.data?.delay_days;
  const storedDelay = Number(rawDelay ?? 0);
  const isDelayed =
    rawDelay !== undefined &&
    rawDelay !== null &&
    String(rawDelay).trim() !== "" &&
    storedDelay !== 0;

  // A value the form cannot produce — the backend now rejects one, but older
  // rows and the API exist. Show it as it really is rather than quietly
  // rounding 90 down to 15, which would tell the merchant their card does
  // something it does not. It cannot be saved, so the error names it.
  const delayUnsupported =
    isDelayed &&
    !(
      Number.isInteger(storedDelay) &&
      storedDelay >= MIN_DELAY_DAYS &&
      storedDelay <= MAX_DELAY_DAYS
    );
  const delayDaysValue = isDelayed && !delayUnsupported ? storedDelay : DEFAULT_DELAY_DAYS;

  // Switching back to Immediately writes a real 0 rather than dropping the
  // key: the merchant chose it, and the saved record should say so.
  const setDelayDays = (days) =>
    setFormData((prev) => ({
      ...prev,
      data: { ...prev.data, delay_days: days },
    }));

  const pointRequiredKinds = [
    "notifications.auto.birthday",
    "notifications.auto.reach_points",
  ];
  const starterAllowedAutoTriggers = [
    "notifications.auto.geo_push",
    "notifications.auto.geo_push_reward",
  ];

  // The two geo triggers are not pushed by the server: their text is written
  // into the pass itself (Apple `locations`, Google `merchantLocations`) and
  // the phone decides when to show it. Nothing about them changes on a
  // customer's card until that card is next rebuilt, so an on/off switch
  // would promise an immediacy it cannot deliver. It is not offered for them.
  const geoTriggers = starterAllowedAutoTriggers;
  const isGeoTrigger = (title) => geoTriggers.includes(title);

  const groupNotificationsByType = (notifications) => {
    // Initialize with all notification types
    const grouped = notificationTypes.reduce((acc, type) => {
      acc[type] = [];
      return acc;
    }, {});

    // Add existing notifications
    notifications?.forEach((notification) => {
      if (notification.kind !== "single") {
        grouped[notification.kind].push(notification);
      }
    });

    return grouped;
  };

  // Update the filtered notifications logic
  const filteredNotifications = notifications?.filter(
    (notification) =>
      notification.kind !== "single" &&
      (selectedPassId === "all" ||
        notification.pass?.id?.toString() === selectedPassId ||
        (!notification.pass && selectedPassId === "all")),
  );

  const groupedNotifications = groupNotificationsByType(filteredNotifications);

  // Update the title selection onChange handler
  const handleTitleChange = (e) => {
    const newTitle = e.target.value;
    if (
      isStarterPlan &&
      (formData.kind === "auto" || editingNotification?.kind === "auto") &&
      newTitle &&
      !starterAllowedAutoTriggers.includes(newTitle)
    ) {
      setIsPlanAlertOpen(true);
      return;
    }
    if (newTitle === "notifications.auto.geo_push" && branches.length === 0) {
      setError(t("notifications.error.noBranches"));
      return;
    }

    if (
      newTitle === "notifications.auto.geo_push_reward" &&
      branches.length === 0
    ) {
      setError(t("notifications.error.noBranches"));
      return;
    }

    setError("");
    setFormData((prev) => {
      const ours = !prev.description || prev.description === prefillFor(prev.title);
      return {
        ...prev,
        title: newTitle,
        description: ours ? prefillFor(newTitle) : prev.description,
      };
    });
  };

  if (isLoadingNotifications) {
    return <div className="p-4">{t("common.loading")}</div>;
  }

  const notificationDescriptions = {
    auto: t("notifications.descriptions.auto"),
    default: t("notifications.descriptions.default"),
  };

  return (
    <div className="max-w-8xl mx-auto space-y-6 lg:space-y-8">
      <Header
        icon={BellIcon}
        title={t("notifications.title")}
        description={t("notifications.description")}
        rightSection={
          <div className="flex items-center space-x-4">
            <select
              value={selectedPassId}
              onChange={(e) => setSelectedPassId(e.target.value)}
              className="rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500"
            >
              <option value="all">{t("notifications.filter.allPasses")}</option>
              {passes?.map((pass) => (
                <option key={pass.id} value={pass.id}>
                  {pass.pass.name}
                </option>
              ))}
            </select>
            {canManageStore && (
              <Button onClick={() => handleOpenModal()} icon={PlusIcon}>
                {t("notifications.new.title")}
              </Button>
            )}
          </div>
        }
      />

      {notificationTypes.map((type) => (
        <div key={type}>
          <h2 className="text-lg font-medium text-gray-900">
            {t(`notifications.kinds.${type}`)} {t("notifications.title")}
          </h2>
          <p className="text-sm text-gray-500 mb-4">
            {notificationDescriptions[type]}
          </p>
          <div className="mt-4 overflow-x-auto">
            {groupedNotifications[type]?.length > 0 ? (
              <table className="min-w-full divide-y divide-gray-300 text-sm text-left">
                <thead className="bg-gray-50">
                  <tr className="font-semibold text-gray-900">
                    <th scope="col" className="p-3 pl-4">
                      {t("notifications.table.pass")}
                    </th>
                    <th scope="col" className="p-3">
                      {t("notifications.table.trigger")}
                    </th>
                    <th scope="col" className="p-3">
                      {t("notifications.table.description")}
                    </th>
                    <th scope="col" className="p-3">
                      {t("notifications.table.status")}
                    </th>
                    <th scope="col"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 bg-white">
                  {groupedNotifications[type].map((notification) => (
                    <tr key={notification.id} className="text-gray-500">
                      <td className="p-3 pl-4">
                        {notification.pass
                          ? `${notification.pass.pass.name}`
                          : t("notifications.filter.allPasses")}
                      </td>
                      <td className="p-3">{t(`${notification.title}`)}</td>
                      <td className="p-3">{notification.description}</td>
                      <td className="p-3">
                        <span
                          className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ${
                            notification.is_active === false
                              ? "bg-gray-100 text-gray-600"
                              : "bg-green-50 text-green-700"
                          }`}
                        >
                          {notification.is_active === false
                            ? t("notifications.status.off")
                            : t("notifications.status.on")}
                        </span>
                      </td>
                      <td className="p-3 pr-4 whitespace-nowrap">
                        {/* Flex, not inline + space-x: on a phone-width column the
                            two inline buttons wrapped onto separate lines and sat
                            at different heights. */}
                        <div
                          className={`flex items-center justify-end gap-3 ${canManageStore ? "" : "hidden"}`}
                        >
                          <button
                            onClick={() => handleOpenModal(notification)}
                            className="text-indigo-600 hover:text-indigo-900"
                          >
                            <PencilIcon className="h-5 w-5" aria-hidden="true" />
                          </button>
                          {notification.kind !== "default" && (
                            <button
                              onClick={() => {
                                setIsDeleteAlertOpen(true);
                                setDeleteNotificationId(notification.id);
                              }}
                              className="text-red-600 hover:text-red-900"
                            >
                              <TrashIcon className="h-5 w-5" aria-hidden="true" />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <EmptyState icon={BellIcon} />
            )}
          </div>
        </div>
      ))}

      <Drawer
        open={isModalOpen}
        setOpen={setIsModalOpen}
        header={t(editingNotification ? "notifications.edit.title" : "notifications.new.title")}
        isForm={true}
        onSubmit={handleSubmit}
        onCancel={() => {
          setIsModalOpen(false);
          setEditingNotification(null);
          setError(null);
        }}
        submitText={t("common.save")}
        cancelText={t("common.cancel")}
        isLoading={updateNotificationMutation.isPending}
        submitDisabled={updateNotificationMutation.isPending}
      >
        <div className="px-4 py-6 sm:px-6 space-y-4">
          {isGeoTrigger(formData.title) ? (
            <p className="rounded-md border border-gray-200 bg-gray-50 p-3 text-sm text-gray-500">
              {t("notifications.form.geoOnCard")}
            </p>
          ) : (
          <Switch.Group
            as="div"
            className="flex items-center justify-between rounded-md border border-gray-200 p-3"
          >
            <span className="flex flex-col pr-4">
              <Switch.Label
                as="span"
                className="text-sm font-medium text-gray-700"
                passive
              >
                {t("notifications.form.isActive")}
              </Switch.Label>
              <Switch.Description as="span" className="text-sm text-gray-500">
                {t("notifications.form.isActiveHint")}
              </Switch.Description>
            </span>
            <Switch
              checked={formData.is_active}
              onChange={(checked) =>
                setFormData((prev) => ({ ...prev, is_active: checked }))
              }
              className={`${
                formData.is_active ? "bg-indigo-600" : "bg-gray-200"
              } relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-hidden focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2`}
            >
              <span
                aria-hidden="true"
                className={`${
                  formData.is_active ? "translate-x-5" : "translate-x-0"
                } pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow-sm ring-0 transition duration-200 ease-in-out`}
              />
            </Switch>
          </Switch.Group>
          )}
          {/* A default notification's card and trigger are fixed, so they are
              shown as plain text rather than a disabled select: a greyed
              dropdown still looks clickable and reads as a broken control. */}
          <div>
            <label
              htmlFor="pass"
              className="block text-sm font-medium text-gray-700"
            >
              {t("notifications.form.pass")}
            </label>
            {isDefaultNotification ? (
              <ReadOnlyValue>
                {editingNotification?.pass?.pass?.name ??
                  t("notifications.filter.allPasses")}
              </ReadOnlyValue>
            ) : (
              <select
                id="pass"
                name="pass_id"
                value={formData.pass_id}
                disabled={editingNotification?.kind === "default"}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    pass_id: e.target.value,
                  }))
                }
                className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                required
              >
                <option value="">{t("notifications.form.selectPass")}</option>
                <option value="all">
                  {t("notifications.filter.allPasses")}
                </option>
                {passes.map((pass) => (
                  <option key={pass.id} value={pass.id}>
                    {pass.pass.name}
                  </option>
                ))}
              </select>
            )}
          </div>
          <div
            className={`${editingNotification?.kind === "default" ? "hidden" : ""}`}
          >
            <label
              htmlFor="kind"
              className="block text-sm font-medium text-gray-700"
            >
              {t("notifications.form.kind")}
            </label>
            <select
              id="kind"
              name="kind"
              value={formData.kind}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  kind: e.target.value,
                }))
              }
              className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
              disabled={editingNotification?.kind === "default"}
            >
              <option value="auto">{t("notifications.kinds.auto")}</option>
              {editingNotification?.kind === "default" && (
                <option value="default">
                  {t("notifications.kinds.default")}
                </option>
              )}
            </select>
          </div>
          <div>
            <div>
              <label
                htmlFor="title"
                className="block text-sm font-medium text-gray-700"
              >
                {t("notifications.form.trigger")}
              </label>
              {isDefaultNotification ? (
                /* t() resolves whatever title the backend wrote, so this needs
                   no option list mirroring app-backend's pass types. */
                <ReadOnlyValue>{t(formData.title)}</ReadOnlyValue>
              ) : (
                <select
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleTitleChange}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                  required
                  disabled={formData.kind === "default"}
                >
                  <option value="">
                    {t("notifications.form.selectTrigger")}
                  </option>
                  <option value="notifications.auto.geo_push">
                    {t("notifications.auto.geo_push")}
                  </option>
                  <option value="notifications.auto.geo_push_reward">
                    {t("notifications.auto.geo_push_reward")}
                  </option>
                  <option value="notifications.auto.birthday">
                    {t("notifications.auto.birthday")}
                  </option>
                  <option value="notifications.auto.reach_points">
                    {t("notifications.auto.reach_points")}{" "}
                  </option>
                  <option value="notifications.auto.eligible_reward">
                    {t("notifications.auto.eligible_reward")}{" "}
                  </option>
                  <option value="notifications.auto.tier_changes">
                    {t("notifications.auto.tier_changes")}{" "}
                  </option>
                  <option value="notifications.auto.points_expiring">
                    {t("notifications.auto.points_expiring")}{" "}
                  </option>
                </select>
              )}
              {error && (
                <div className="mt-2 text-sm text-red-600">
                  {error}{" "}
                  <Link
                    to="/settings"
                    className="text-indigo-600 hover:text-indigo-800"
                  >
                    {t("notifications.error.addBranch")}
                  </Link>
                </div>
              )}
            </div>
          </div>
          <div>
            <label
              htmlFor="description"
              className="block text-sm font-medium text-gray-700"
            >
              {t("notifications.form.description")}
            </label>
            <textarea
              id="description"
              name="description"
              ref={descriptionRef}
              value={formData.description}
              onChange={(e) =>
                setFormData((prev) => ({
                  ...prev,
                  description: e.target.value,
                }))
              }
              rows={3}
              className="mt-1 block w-full resize-none overflow-hidden rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
              required
            />
            {placeholdersFor(formData.title).length > 0 && (
              <div className="mt-2 flex flex-wrap items-center gap-x-1.5 gap-y-1.5 text-xs text-gray-500">
                <span>{t("notifications.form.placeholders")}</span>
                {placeholdersFor(formData.title).map((name) => (
                  <button
                    type="button"
                    key={name}
                    onClick={() => insertPlaceholder(name)}
                    title={t(`notifications.placeholders.${name}`)}
                    className="rounded border border-gray-300 bg-gray-50 px-1.5 py-0.5 font-mono text-xs text-gray-700 hover:border-indigo-500 hover:text-indigo-700 focus:outline-hidden focus:ring-2 focus:ring-indigo-500"
                  >
                    %{name}%
                  </button>
                ))}
              </div>
            )}
          </div>
          <div>
            <div className="flex flex-wrap gap-4">
              {pointRequiredKinds.includes(formData.title) && (
                <div className="flex-1 min-w-[200px]">
                  <label
                    htmlFor="point"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {formData.title === "notifications.auto.birthday"
                      ? t("notifications.form.pointReward")
                      : t("notifications.form.pointThreshold")}
                  </label>
                  <input
                    type="number"
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    id="point"
                    name="point"
                    value={formData.data?.points || 0}
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        data: {
                          ...prev.data,
                          points: e.target.value,
                        },
                      }))
                    }
                  />
                </div>
              )}
              {formData.title === "notifications.auto.birthday" && (
                <div className="flex-1 min-w-[200px]">
                  <label
                    htmlFor="expire_after"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("notifications.form.expireAfter")}
                  </label>
                  <select
                    name="expire_after"
                    id="expire_after"
                    value={formData.data?.expire_after || 0}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        data: {
                          ...prev.data,
                          expire_after: e.target.value,
                        },
                      }))
                    }
                  >
                    <option value="0">
                      {t("notifications.form.noExpiration")}
                    </option>
                    <option value="1">{t("notifications.form.1Day")}</option>
                    <option value="7">{t("notifications.form.7Days")}</option>
                    <option value="14">{t("notifications.form.14Days")}</option>
                    <option value="30">{t("notifications.form.30Days")}</option>
                  </select>
                </div>
              )}
              {formData.title === "notifications.auto.points_expiring" && (
                <div className="flex-1 min-w-[200px]">
                  <label
                    htmlFor="expiry_lead_days"
                    className="block text-sm font-medium text-gray-700"
                  >
                    {t("notifications.form.expiryLeadDays")}
                  </label>
                  <select
                    name="expiry_lead_days"
                    id="expiry_lead_days"
                    value={formData.data?.days || 7}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-xs focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                    onChange={(e) =>
                      setFormData((prev) => ({
                        ...prev,
                        data: {
                          ...prev.data,
                          days: e.target.value,
                        },
                      }))
                    }
                  >
                    <option value="1">{t("notifications.form.1Day")}</option>
                    <option value="7">{t("notifications.form.7Days")}</option>
                    <option value="14">{t("notifications.form.14Days")}</option>
                    <option value="30">{t("notifications.form.30Days")}</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    {reminderSchedule}
                  </p>
                </div>
              )}
              {/* Only this trigger. The other two keep their presets, and
                  the backend ignores this key for every other title.

                  min-w-0 and break-words throughout: this lives in a flex row
                  inside a drawer that must never scroll sideways, and a flex
                  child's default minimum width is its content — which is how
                  one long translation pushes a panel wider than itself. */}
              {formData.title === "notifications.auto.reach_points" && (
                <fieldset className="w-full min-w-0">
                  <legend className="block text-sm font-medium text-gray-700 [overflow-wrap:anywhere]">
                    {t("notifications.form.sendTiming")}
                  </legend>

                  <div className="mt-2 space-y-2">
                    {[
                      ["immediate", "notifications.form.immediately"],
                      ["delayed", "notifications.form.afterDelay"],
                    ].map(([value, label]) => (
                      <label
                        key={value}
                        className="flex items-start gap-2 text-sm text-gray-900"
                      >
                        <input
                          type="radio"
                          name="send_timing"
                          value={value}
                          checked={(value === "delayed") === isDelayed}
                          onChange={() =>
                            setDelayDays(
                              value === "delayed" ? DEFAULT_DELAY_DAYS : 0,
                            )
                          }
                          className="mt-0.5 h-4 w-4 shrink-0 border-gray-300 text-indigo-600 focus:ring-indigo-500"
                        />
                        <span className="min-w-0 break-words [overflow-wrap:anywhere]">
                          {t(label)}
                        </span>
                      </label>
                    ))}
                  </div>

                  {isDelayed && (
                    <div className="mt-3 min-w-0">
                      <label
                        htmlFor="delay_days"
                        className="block text-sm font-medium text-gray-700 [overflow-wrap:anywhere]"
                      >
                        {t("notifications.form.delayDaysLabel")}
                      </label>
                      <div className="mt-1 flex items-center gap-2">
                        <select
                          id="delay_days"
                          name="delay_days"
                          value={delayUnsupported ? String(rawDelay) : delayDaysValue}
                          onChange={(e) => setDelayDays(Number(e.target.value))}
                          className={`w-28 shrink-0 rounded-md shadow-xs sm:text-sm ${
                            delayUnsupported
                              ? "border-red-300 focus:border-red-500 focus:ring-red-500"
                              : "border-gray-300 focus:border-indigo-500 focus:ring-indigo-500"
                          }`}
                        >
                          {delayUnsupported && (
                            <option value={String(rawDelay)} disabled>
                              {String(rawDelay)}
                            </option>
                          )}
                          {DELAY_DAY_OPTIONS.map((days) => (
                            <option key={days} value={days}>
                              {days}
                            </option>
                          ))}
                        </select>
                        <span className="min-w-0 break-words text-sm text-gray-700 [overflow-wrap:anywhere]">
                          {t("notifications.form.daysUnit")}
                        </span>
                      </div>
                      <p
                        className={`mt-2 break-words text-xs [overflow-wrap:anywhere] ${
                          delayUnsupported ? "text-red-600" : "text-gray-500"
                        }`}
                      >
                        {delayUnsupported
                          ? t("notifications.form.delayUnsupported", {
                              min: MIN_DELAY_DAYS,
                              max: MAX_DELAY_DAYS,
                            })
                          : t(
                              delayDaysValue === 1
                                ? "notifications.form.delayHintOne"
                                : "notifications.form.delayHint",
                              { count: delayDaysValue },
                            )}
                      </p>
                    </div>
                  )}
                </fieldset>
              )}
            </div>
          </div>
        </div>
      </Drawer>

      <DeleteAlert
        isOpen={isDeleteAlertOpen}
        onClose={() => setIsDeleteAlertOpen(false)}
        onConfirm={() => {
          deleteNotificationMutation.mutate({
            storeId: selectedStore.id,
            notificationId: deleteNotificationId,
          });
          setIsDeleteAlertOpen(false);
        }}
      />
      <TrialEndedAlert
        isOpen={isTrialEndedAlertOpen}
        onClose={() => setIsTrialEndedAlertOpen(false)}
        subscriptionStatus={selectedStore?.subscription_status}
      />
      <PlanAlert
        isOpen={isPlanAlertOpen}
        onClose={() => setIsPlanAlertOpen(false)}
        title={t("common.planAlertTitle")}
        description={t("notifications.planRestriction.starter", {
          plan: t("pricing.tier.growth.name"),
        })}
        plan="growth"
      />
    </div>
  );
}
